// src/pages/dashboard/branch/settings/componenets/FeaturesSettings.tsx
// NOTE: The Supabase update_branch SQL function must include:
//   languages = COALESCE((payload->>'languages')::jsonb, languages),
// in its UPDATE statement for language changes to persist.
import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../../../../components/ui/Card';
import Toggle from '../../../../../components/ui/Toggle';
import { RestaurantTheme } from '../../../../../types/restaurant';
import { Branch } from '../../../../../utils/supabaseClient';
import { ThemeType } from '../../../../../types/custom';

type FeaturesSettingsProps = {
	restaurant: Branch | null;
	updateTheme: (updates: Partial<RestaurantTheme>) => void;
	updateRestaurant: (updates: Partial<Branch>) => void;
};

interface LanguageEntry {
	id: number;
	code: string;
	language: string;
}

interface LanguagesData {
	data: LanguageEntry[];
	is_multilanguage: boolean;
}

const ALL_LANGUAGES: LanguageEntry[] = [
	{ id: 1, code: 'en', language: 'english' },
	{ id: 2, code: 'ar', language: 'arabic' },
];

const FeaturesSettings: React.FC<FeaturesSettingsProps> = ({ restaurant, updateTheme, updateRestaurant }) => {
	const { t } = useTranslation();

	const languagesData = restaurant?.languages as LanguagesData | null;
	const allowedCodes: string[] = languagesData?.data?.map((l) => l.code) ?? ['en'];

	const handleLanguageToggle = (code: string) => {
		const current = languagesData?.data ?? [{ id: 1, code: 'en', language: 'english' }];
		const isSelected = current.some((l) => l.code === code);

		if (isSelected && current.length === 1) return;

		const updated = isSelected
			? current.filter((l) => l.code !== code)
			: [...current, ALL_LANGUAGES.find((l) => l.code === code)!];

		const final = updated.length === 0 ? [{ id: 1, code: 'en', language: 'english' }] : updated;

		updateRestaurant({
			languages: {
				data: final,
				is_multilanguage: final.length > 1,
			} as unknown as Branch['languages'],
		});
	};

	return (
		<div className="space-y-6">
			<Card>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
					{t('settings.tabs.features')}
				</h3>
				<div className="space-y-4">
					{/* Allowed Languages */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('settings.allowedLanguages')}
						</label>
						<div className="grid gap-x-5 gap-y-3" style={{ gridTemplateColumns: 'max-content 1fr' }}>
							{ALL_LANGUAGES.map((lang) => {
								const isSelected = allowedCodes.includes(lang.code);
								const isDisabled = isSelected && allowedCodes.length === 1;
								return (
									<React.Fragment key={lang.code}>
										<span className={`text-sm text-gray-700 dark:text-gray-300 self-center ${isDisabled ? 'opacity-50' : ''}`}>
											{t(`settings.languageOptions.${lang.code}`)}
										</span>
										<div className={`flex items-center gap-5 ${isDisabled ? 'opacity-50' : ''}`}>
											<Toggle
												enabled={isSelected}
												onChange={() => !isDisabled && handleLanguageToggle(lang.code)}
											/>
										</div>
									</React.Fragment>
								);
							})}
						</div>
					</div>

					{/* One Page Layout */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('settings.onePageLayout')}
						</label>
						<div className="flex flex-wrap gap-2 sm:gap-3">
							{[true, false].map((value) => (
								<button
									key={value ? 'yes' : 'no'}
									onClick={() => updateTheme({ isOnePageLayout: value })}
									className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.isOnePageLayout === value
										? 'border-primary bg-primary/10 text-primary'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
										}`}
								>
									{t(`settings.onePageLayoutOptions.${value ? 'yes' : 'no'}`)}
								</button>
							))}
						</div>
					</div>

					{/* Enable Item Feedback & Rating */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('settings.isFeedbackEnabled')}
						</label>
						<div className="flex flex-wrap gap-2 sm:gap-3">
							{[true, false].map((value) => (
								<button
									key={value ? 'yes' : 'no'}
									onClick={() => updateTheme({ isFeedbackEnabled: value })}
									className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.isFeedbackEnabled === value
										? 'border-primary bg-primary/10 text-primary'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
										}`}
								>
									{t(`settings.isFeedbackEnabledOptions.${value ? 'yes' : 'no'}`)}
								</button>
							))}
						</div>
					</div>

					{/* Show Feedback on Items */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('settings.isFeedbackPubliclyVisible')}
						</label>
						<div className="flex flex-wrap gap-2 sm:gap-3">
							{[true, false].map((value) => (
								<button
									key={value ? 'yes' : 'no'}
									onClick={() => updateTheme({ isFeedbackPubliclyVisible: value })}
									className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.isFeedbackPubliclyVisible === value
										? 'border-primary bg-primary/10 text-primary'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
										}`}
								>
									{t(`settings.isFeedbackPubliclyVisibleOptions.${value ? 'yes' : 'no'}`)}
								</button>
							))}
						</div>
					</div>
				</div>
			</Card>
		</div>
	);
};

export default FeaturesSettings;
